import { isNil } from 'ramda';
import React from 'react';
import { compose, withHandlers, withProps } from 'recompose';
import PropTypes from 'prop-types';
import { FormSection } from 'redux-form';
import { t } from 'js/modules/infra/i18n';
import RangeSelect from '../../rangeSelect';
import withMinuteOptions, { minuteOptionShape } from '../../hoc/withMinuteOptions';
import SelectWithIndeterminate from '../../SelectWithIndeterminate';
import { INDETERMINATE } from '../../constants';

const HoursMinsSelect = ({
  name,
  label,
  customHours,
  getMaxHours,
  getMinuteOptions,
  // eslint-disable-next-line react/prop-types
  'data-automation-id': dataAutomationId,
}) => (
  <div>
    <span className="c-label-new">
      {label}
    </span>
    <span>
      <FormSection name={name}>
        <RangeSelect
          name="hours"
          wrapperClassName="u-inline-block u-align-middle u-mv-- u-mr-"
          triggerClassName="c-select--small u-pr+"
          dropdownClassName="c-select-scrollable-dropdown"
          valueSuffix={`${t('admin:shops.time.hours')}`}
          start={0}
          end={getMaxHours()}
          customOption={customHours.map(hour => ({ 
            value: hour, 
            label: `${hour}${t('admin:shops.time.hours')}` 
          }))}
          data-automation-id={`${dataAutomationId || name}-hours`}
        />
        <SelectWithIndeterminate
          name="minutes"
          wrapperClassName="u-inline-block u-align-middle u-mv--"
          triggerClassName="c-select--small u-pr+"
          dropdownClassName="c-select-scrollable-dropdown"
          options={getMinuteOptions().map(number => ({ 
            value: number, 
            label: `${number} ${t('admin:shops.time.minutes')}` 
          }))}
          data-automation-id={`${dataAutomationId || name}-minutes`}
        />
      </FormSection>
    </span>
  </div>
);

HoursMinsSelect.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  current: PropTypes.shape({
    hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  max: PropTypes.shape({
    hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    minutes: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
  minuteOptions: minuteOptionShape.isRequired,
  customHours: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  getMaxHours: PropTypes.func.isRequired,
  getMinuteOptions: PropTypes.func.isRequired,
};

HoursMinsSelect.defaultProps = {
  current: {},
  max: {},
  customHours: [],
  label: null,
};

export default compose(
  withMinuteOptions,
  withProps(props => ({
    max: props.max || {},
    current: props.current || {},
  })),
  withHandlers({
    getMaxHours: props => () => {
      const { max } = props;
      if (isNil(max.hours)) return 25;
      if (Number(max.minutes) === 0) return Number(max.hours);
      return Number(max.hours) + 1;
    },
    getMinuteOptions: props => () => {
      const { max, current, minuteOptions } = props;
      if (isNil(max.hours) || current.hours === INDETERMINATE || Number(max.hours) !== Number(current.hours)) {
        return minuteOptions;
      }

      return minuteOptions.filter(minutes => Number(minutes) < Number(max.minutes));
    },
  }),
)(HoursMinsSelect);
